import { ensureSwiper, events, fetchSection, focusQuietly, openDialog, parseHTML, prefersReducedMotion } from './theme.js';

// Put focus back on a re-rendered control. Skipped for <select> on touch devices:
// focusing a select there reopens the native picker, so it would pop up again after every pick.
function refocus(el) {
  if (el.tagName === 'SELECT' && window.matchMedia('(pointer: coarse)').matches) return;
  focusQuietly(el);
}

class ProductInfo extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    this.abort = null;
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  onChange = (e) => {
    const input = e.target;
    if (!input.closest('variant-picker')) return;
    const picker = input.closest('variant-picker');
    const selected = Array.from(picker.querySelectorAll('input[type="radio"]:checked, select option:checked'))
      .map((el) => el.dataset.optionValueId)
      .filter(Boolean);
    const optionUrl = (input.tagName === 'SELECT' ? input.selectedOptions[0] : input).dataset.productUrl;
    const productUrl = optionUrl || this.dataset.productUrl;
    const productChanged = optionUrl && optionUrl !== this.dataset.productUrl;

    const fromHere = e.isTrusted && this.contains(document.activeElement);
    this.render(productUrl, selected, productChanged, fromHere ? input : null);
  };

  whenReady() {
    return this.pending || Promise.resolve();
  }

  render(url, optionValues, productChanged, trigger) {
    const run = this.renderNow(url, optionValues, productChanged, trigger);
    const pending = run.finally(() => {
      if (this.pending === pending) this.pending = null;
    });
    this.pending = pending;
    return pending;
  }

  async renderNow(url, optionValues, productChanged, trigger) {
    this.abort?.abort();
    this.abort = new AbortController();
    const u = new URL(url, window.location.origin);
    if (optionValues.length) u.searchParams.set('option_values', optionValues.join(','));
    this.setAttribute('aria-busy', 'true');
    let doc;
    try {
      doc = await fetchSection(u.pathname + u.search, this.sectionId, { signal: this.abort.signal });
    } catch (err) {
      if (err.name !== 'AbortError') this.removeAttribute('aria-busy');
      return;
    }
    this.removeAttribute('aria-busy');
    const incoming = doc.querySelector(`product-info[data-section-id="${this.sectionId}"]`);
    if (!incoming) return;
    this.updateOutside(doc, productChanged);
    const focusKey = trigger?.id;

    if (productChanged) {

      this.replaceChildren(...Array.from(incoming.childNodes).map((n) => document.importNode(n, true)));
      this.dataset.productUrl = incoming.dataset.productUrl;
    } else {
      incoming.querySelectorAll('[data-update]').forEach((fresh) => {
        const key = fresh.dataset.update;
        this.querySelectorAll(`[data-update="${key}"]`).forEach((current) => {
          if (current.closest('product-info') !== this) return;
          current.replaceWith(document.importNode(fresh, true));
        });
      });
    }

    const variantId = incoming.dataset.variantId;
    this.dataset.variantId = variantId || '';
    if (focusKey) {
      const restore = document.getElementById(focusKey);
      if (restore && this.contains(restore)) refocus(restore);
    }

    if (this.hasAttribute('data-update-url')) {
      const next = new URL(incoming.dataset.productUrl || u.pathname, window.location.origin);
      if (variantId) next.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', next.pathname + next.search);
    }

    const mediaId = incoming.dataset.featuredMediaId;
    if (mediaId) this.querySelector('media-gallery')?.goTo(mediaId, false);

    events.emit('variant:changed', { sectionId: this.sectionId, variantId, productUrl: incoming.dataset.productUrl });
  }

  updateOutside(doc, productChanged) {
    const scope = this.closest('.shopify-section') || this.parentElement;
    if (!scope) return;
    if (productChanged) {

      const bar = doc.querySelector('sticky-buy-bar');
      const current = scope.querySelector('sticky-buy-bar');
      if (bar && current) current.replaceWith(document.importNode(bar, true));
      return;
    }
    doc.querySelectorAll('[data-update]').forEach((fresh) => {
      if (fresh.closest('product-info')) return;
      scope.querySelectorAll(`[data-update="${fresh.dataset.update}"]`).forEach((current) => {
        if (!current.closest('product-info')) current.replaceWith(document.importNode(fresh, true));
      });
    });
  }
}
customElements.define('product-info', ProductInfo);

class VariantPicker extends HTMLElement {}
customElements.define('variant-picker', VariantPicker);

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.thumbs = this.querySelector('[data-gallery-thumbs]');
    this.counter = this.querySelector('[data-gallery-counter]');
    this.thumbs?.addEventListener('click', (e) => {
      const b = e.target.closest('[data-media-target]');
      if (b) this.goTo(b.dataset.mediaTarget);
    });
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));
    this.initLightbox();
    this.sync();
    this.initSwiper().then(() => this.sync());
  }

  disconnectedCallback() {
    this.swiper?.destroy(true, true);
    this.swiper = null;
  }

  get items() {
    return Array.from(this.track?.querySelectorAll('[data-media-id]') || []);
  }

  async initSwiper() {
    if (!this.track || this.items.length < 1) return;
    const Swiper = await ensureSwiper();
    if (!Swiper || !this.isConnected || this.swiper) return;

    const viewport = document.createElement('div');
    viewport.className = 'swiper gallery__viewport';
    this.track.replaceWith(viewport);
    viewport.append(this.track);

    this.track.classList.add('swiper-wrapper');
    this.items.forEach((el) => el.classList.add('swiper-slide'));

    const horizontalOnDesktop = this.classList.contains('gallery--carousel');
    this.swiper = new Swiper(viewport, {
      slidesPerView: 'auto',
      spaceBetween: 8,
      speed: prefersReducedMotion() ? 0 : 350,
      grabCursor: true,
      threshold: 4,
      watchSlidesProgress: true,
      keyboard: { enabled: true, onlyInViewport: true },
      a11y: { enabled: true, slideRole: /^(UL|OL)$/.test(this.track.tagName) ? 'listitem' : 'group' },
      breakpoints: { 990: { enabled: horizontalOnDesktop, spaceBetween: 0 } },
    });

    this.closest('dialog')?.addEventListener('dialog:open', () => this.swiper?.update());
    this.swiper.on('slideChange', () => {
      const item = this.items[this.swiper.activeIndex];
      if (item) this.setActive(item.dataset.mediaId);
      this.updateCounter(this.swiper.activeIndex);
    });
  }

  current() {
    return this.swiper?.enabled ? this.swiper.activeIndex : 0;
  }

  step(dir) {
    if (this.swiper?.enabled) {
      dir > 0 ? this.swiper.slideNext() : this.swiper.slidePrev();
      return;
    }
    const i = Math.max(0, Math.min(this.items.length - 1, this.current() + dir));
    this.scrollTo(this.items[i]);
  }

  goTo(mediaId, scroll = true) {
    const item = this.items.find((el) => el.dataset.mediaId === String(mediaId));
    if (!item) return;
    if (this.swiper?.enabled) {
      this.swiper.slideTo(this.items.indexOf(item));
      this.setActive(item.dataset.mediaId);
      return;
    }
    if (scroll) {
      this.scrollTo(item);
      return;
    }
    this.setActive(item.dataset.mediaId);
  }

  scrollTo(item) {
    if (!item) return;
    const behavior = prefersReducedMotion() ? 'auto' : 'smooth';
    const r = item.getBoundingClientRect();
    if (r.top < 0 || r.top > window.innerHeight) item.scrollIntoView({ behavior, block: 'start' });
    this.setActive(item.dataset.mediaId);
  }

  sync() {
    const i = this.current();
    const item = this.items[i];
    if (item) this.setActive(item.dataset.mediaId);
    this.updateCounter(i);
  }

  updateCounter(i) {
    if (this.counter) this.counter.textContent = `${i + 1} / ${this.items.length}`;
  }

  setActive(id) {
    this.items.forEach((el) => {
      const active = el.dataset.mediaId === String(id);
      el.toggleAttribute('data-active', active);
      if (!active) el.querySelectorAll('video').forEach((v) => v.pause());
    });
    this.thumbs?.querySelectorAll('[data-media-target]').forEach((t) => {
      const current = t.dataset.mediaTarget === String(id);
      t.setAttribute('aria-current', String(current));
      if (current) this.revealThumb(t);
    });
  }

  // scroll only the thumbnail strip (not the page) so the active thumb stays centred
  revealThumb(thumb) {
    const strip = this.thumbs;
    if (!strip || strip.scrollWidth <= strip.clientWidth && strip.scrollHeight <= strip.clientHeight) return;
    const s = strip.getBoundingClientRect();
    const t = thumb.getBoundingClientRect();
    const vertical = strip.scrollHeight > strip.clientHeight && strip.scrollWidth <= strip.clientWidth;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (vertical) {
      strip.scrollTo({ top: strip.scrollTop + (t.top - s.top) - (s.height - t.height) / 2, behavior });
    } else {
      strip.scrollTo({ left: strip.scrollLeft + (t.left - s.left) - (s.width - t.width) / 2, behavior });
    }
  }

  initLightbox() {
    this.lightbox = this.querySelector('[data-lightbox]');
    if (!this.lightbox) return;
    this.lbSlides = Array.from(this.lightbox.querySelectorAll('[data-lightbox-main] .swiper-slide'));
    this.lbThumbButtons = Array.from(this.lightbox.querySelectorAll('[data-lightbox-index]'));
    this.lbCounter = this.lightbox.querySelector('[data-lightbox-counter]');

    // Capture phase: runs before a video's own play button, so videos open in the viewer instead of inline.
    this.addEventListener('click', this.onMediaClick, true);
    this.lightbox.addEventListener('click', (e) => {
      const thumb = e.target.closest('[data-lightbox-index]');
      if (thumb) this.lbMain?.slideTo(Number(thumb.dataset.lightboxIndex));
    });
    this.lightbox.addEventListener('dialog:close', () => {
      this.lbMain?.zoom?.out();
      this.lbMain?.keyboard?.disable();
      this.swiper?.keyboard?.enable();
      this.lbSlides.forEach((slide) => this.unloadLightboxVideo(slide));
      const id = this.lbSlides[this.lbMain?.activeIndex ?? -1]?.dataset.mediaId;
      if (id) this.goTo(id, false);
    });
  }

  onMediaClick = (e) => {
    if (!this.lightbox || e.target.closest('[data-lightbox]')) return;
    // No viewer on phones/tablets: images just swipe in the gallery, videos play inline.
    if (window.matchMedia('(max-width: 989px)').matches) return;
    const item = e.target.closest('[data-media-id]');
    if (!item || !this.track?.contains(item)) return;
    if (!e.target.closest('[data-lightbox-open], deferred-media:not([data-model]):not([loaded]) > button')) return;
    if (this.swiper && this.swiper.allowClick === false) return;
    e.preventDefault();
    e.stopPropagation();
    this.openLightbox(item.dataset.mediaId, e.target.closest('button'));
  };

  async openLightbox(mediaId, opener) {
    const index = this.lbSlides.findIndex((s) => s.dataset.mediaId === String(mediaId));
    if (index < 0) return;
    this.items.forEach((el) => el.querySelectorAll('video').forEach((v) => v.pause()));
    this.swiper?.keyboard?.disable();
    openDialog(this.lightbox, opener);

    const Swiper = await ensureSwiper();
    if (!Swiper || !this.lightbox.open) return;
    if (!this.lbMain) {
      this.createLightboxSwipers(Swiper, index);
    } else {
      this.lbThumbsSwiper.update();
      this.lbMain.update();
      this.lbMain.keyboard.enable();
      this.lbMain.slideTo(index, 0);
    }
    this.onLightboxSlide();
  }

  createLightboxSwipers(Swiper, index) {
    const speed = prefersReducedMotion() ? 0 : 450;
    this.lbThumbsSwiper = new Swiper(this.lightbox.querySelector('[data-lightbox-thumbs]'), {
      direction: 'horizontal',
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: { enabled: true, sticky: false },
      watchSlidesProgress: true,
      // a few px of finger jitter must still count as a tap, not a drag
      threshold: 5,
      speed,
      breakpoints: { 990: { direction: 'vertical', spaceBetween: 10 } },
      // Touch taps: Swiper swallows the native click after any drag/momentum, so select on its own tap event.
      on: {
        tap: (s) => {
          if (s.clickedIndex != null && s.clickedIndex >= 0) this.lbMain?.slideTo(s.clickedIndex);
        },
      },
    });
    this.lbMain = new Swiper(this.lightbox.querySelector('[data-lightbox-main]'), {
      initialSlide: index,
      speed,
      spaceBetween: 32,
      grabCursor: true,
      threshold: 6,
      noSwipingSelector: 'video, iframe',
      zoom: { maxRatio: 3, toggle: true },
      keyboard: { enabled: true, onlyInViewport: false },
      navigation: {
        prevEl: this.lightbox.querySelector('[data-lightbox-prev]'),
        nextEl: this.lightbox.querySelector('[data-lightbox-next]'),
      },
      a11y: { enabled: true },
      on: { slideChange: () => this.onLightboxSlide() },
    });
  }

  onLightboxSlide() {
    const i = this.lbMain.activeIndex;
    this.lbSlides.forEach((slide, n) => {
      if (n === i) this.loadLightboxVideo(slide);
      else this.unloadLightboxVideo(slide);
    });
    this.lbThumbButtons.forEach((b, n) => b.setAttribute('aria-current', String(n === i)));
    const thumb = this.lbThumbsSwiper.slides[i];
    if (thumb && !thumb.classList.contains('swiper-slide-fully-visible')) {
      this.lbThumbsSwiper.slideTo(Math.max(0, i - 1));
    }
    if (this.lbCounter) this.lbCounter.textContent = `${i + 1} / ${this.lbSlides.length}`;
  }

  loadLightboxVideo(slide) {
    const template = slide.querySelector('template');
    if (!template || slide.hasAttribute('data-loaded')) return;
    slide.setAttribute('data-loaded', '');
    slide.append(template.content.cloneNode(true));
    slide.querySelector('video')?.play?.().catch(() => {});
  }

  unloadLightboxVideo(slide) {
    if (!slide.hasAttribute('data-loaded')) return;
    slide.removeAttribute('data-loaded');
    slide.querySelectorAll('.media-lightbox__video').forEach((el) => el.remove());
  }
}
customElements.define('media-gallery', MediaGallery);

class PickupAvailability extends HTMLElement {
  connectedCallback() {
    if (!this.dataset.variantId) return;
    const url = `${window.Diecela.routes.root_url.replace(/\/$/, '')}/variants/${this.dataset.variantId}/?section_id=pickup-availability`;
    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        const doc = parseHTML(html);
        const content = doc.querySelector('[data-pickup-content]');
        if (!content || !content.children.length) {
          this.hidden = true;
          return;
        }
        this.replaceChildren(...Array.from(content.childNodes).map((n) => document.importNode(n, true)));
        this.hidden = false;
      })
      .catch(() => (this.hidden = true));
  }
}
customElements.define('pickup-availability', PickupAvailability);

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded) return;
    const load = () => {
      const url = `${this.dataset.url}&section_id=${this.dataset.sectionId}`;
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = parseHTML(html);
          const fresh = doc.querySelector(`product-recommendations[data-key="${this.dataset.key}"]`);
          this.dataset.loaded = 'true';
          if (!fresh || !fresh.querySelector('[data-rec-item]')) {
            this.closest('[data-hide-when-empty]')?.setAttribute('hidden', '');
            this.hidden = true;
            return;
          }
          this.replaceChildren(...Array.from(fresh.childNodes).map((n) => document.importNode(n, true)));
        })
        .catch(() => (this.hidden = true));
    };
    this.io = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.io.disconnect();
      load();
    }, { rootMargin: '400px 0px' });
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.io?.disconnect();
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class StickyBuyBar extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector(this.dataset.watch);
    if (!target) return;
    this.io = new IntersectionObserver(([entry]) => {
      const passed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      this.toggleAttribute('data-visible', passed);
      this.toggleAttribute('inert', !passed);
      this.schedule();
    });
    this.io.observe(target);
    this.ro = new ResizeObserver(() => this.schedule());
    this.ro.observe(this);
    this.mo = new MutationObserver(() => this.schedule());
    this.mo.observe(this, { childList: true, subtree: true });
    this.addEventListener('scroll', this.schedule, { capture: true, passive: true });
    this.sync();
    this.addEventListener('click', (e) => {
      if (!e.target.closest('[data-sticky-add]')) return;
      if (this.dataset.action === 'scroll' && this.scrollToAdd()) return;
      const form = document.getElementById(this.dataset.form);
      form?.requestSubmit();
    });
    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-sticky-option]')) this.pickOption(e.target);
      this.schedule();
    });
  }

  // Brings the main add-to-cart button (the bundle's own button when there is one) into view instead of buying straight away.
  scrollToAdd() {
    const info = document.querySelector(this.dataset.watch) || document;
    const button = [...info.querySelectorAll('.bundle-offer__add, [data-add-to-cart], button[name="add"]')].find((el) => el.offsetParent !== null);
    if (!button) return false;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rect = button.getBoundingClientRect();
    const top = rect.top + window.scrollY - window.innerHeight / 2 + rect.height / 2;
    window.scrollTo({ top: Math.max(0, top), behavior: reduce ? 'auto' : 'smooth' });
    button.focus({ preventScroll: true });
    button.classList.remove('is-attention');
    void button.offsetWidth;
    button.classList.add('is-attention');
    button.addEventListener('animationend', () => button.classList.remove('is-attention'), { once: true });
    return true;
  }

  async pickOption(control) {
    const info = document.querySelector(this.dataset.watch) || document.querySelector('product-info');
    const picker = (info || document).querySelector('variant-picker');
    if (!picker) return;
    const group = picker.querySelectorAll('.variant-option')[Number(control.dataset.stickyOption) - 1];
    if (!group) return;
    const value = control.value;
    const focusKey = control.id;
    const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find((r) => r.value === value);

    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      const dropdown = group.querySelector('select');
      if (!dropdown) return;
      dropdown.value = value;
      dropdown.dispatchEvent(new Event('change', { bubbles: true }));
    }

    await info?.whenReady?.();
    const restore = focusKey && document.getElementById(focusKey);
    if (restore && restore !== control && this.contains(restore)) refocus(restore);
  }

  vars = {};

  schedule = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.sync();
    });
  };

  setVar(name, value) {
    if (this.vars[name] === value) return;
    this.vars[name] = value;
    document.documentElement.style.setProperty(name, value);
  }

  sync() {
    const offset = this.hasAttribute('data-offset');
    const top = this.classList.contains('sticky-buy--top');
    const shown = this.hasAttribute('data-visible');
    const rows = this.querySelectorAll('.sticky-buy__swatches');

    const height = offset || top ? this.offsetHeight : 0;
    const overflow = Array.from(rows, (row) => row.scrollWidth - row.clientWidth - row.scrollLeft > 1);

    if (offset) this.setVar('--sticky-buy-h', `${height}px`);
    if (top) {
      this.setVar('--sticky-buy-top-h', `${shown ? height : 0}px`);
      document.documentElement.classList.toggle('sticky-buy-active', shown);
    }
    rows.forEach((row, i) => row.toggleAttribute('data-overflow', overflow[i]));
  }

  disconnectedCallback() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.vars = {};
    this.io?.disconnect();
    this.ro?.disconnect();
    this.mo?.disconnect();
    if (!document.querySelector('sticky-buy-bar[data-offset]')) {
      document.documentElement.style.removeProperty('--sticky-buy-h');
    }
    if (!document.querySelector('sticky-buy-bar.sticky-buy--top')) {
      document.documentElement.style.removeProperty('--sticky-buy-top-h');
      document.documentElement.classList.remove('sticky-buy-active');
    }
  }
}
customElements.define('sticky-buy-bar', StickyBuyBar);
